import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { formatXAF, formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShoppingBasket, Search, PackageCheck, Languages } from "lucide-react";
import ContactFooter from "@/components/layout/ContactFooter";

const PIPELINE = ["commandée", "confirmée", "préparée", "expédiée", "en_livraison", "livrée"];

export default function Commander() {
  const { t, lang, setLang } = useI18n();
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", city: "", email: "", product_id: "", quantity: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [trackRef, setTrackRef] = useState("");
  const [tracked, setTracked] = useState(null);

  useEffect(() => {
    api.get("/public/catalog").then((r) => setProducts(r.data)).catch(() => {});
    // Suivi auto via ?track=CMD-XXXX (QR code)
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("track");
    if (ref) {
      setTrackRef(ref);
      api.get(`/public/orders/track/${encodeURIComponent(ref)}`)
        .then(({ data }) => setTracked(data))
        .catch(() => {});
    }
  }, []);

  async function submitOrder(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/public/orders", { ...form, quantity: parseFloat(form.quantity || 0) });
      setSuccess(data);
      setForm({ name: "", phone: "", city: "", email: "", product_id: "", quantity: "" });
      setTrackRef("");
      setTracked(null);
      toast.success(t("order_success"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  async function track(e) {
    e.preventDefault();
    setTracked(null);
    try {
      const { data } = await api.get(`/public/orders/track/${encodeURIComponent(trackRef.trim())}`);
      setTracked(data);
    } catch {
      toast.error(t("error"));
    }
  }

  const statusIndex = tracked ? Math.max(0, PIPELINE.indexOf(tracked.status)) : -1;

  return (
    <div className="min-h-screen bg-background animated-texture grain-overlay" data-testid="portal-page">
      <header className="border-b border-border bg-secondary/60">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-agrinova.png" alt="AgriNova" className="w-10 h-10 rounded-lg object-cover" />
            <div>
              <div className="font-bold">AgriNova</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("slogan")}</div>
            </div>
          </div>
          <button
            onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary border border-primary/30 rounded-full px-3 py-1.5"
            data-testid="lang-toggle-portal"
          >
            <Languages className="w-3.5 h-3.5" />{lang === "fr" ? "EN" : "FR"}
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-10">
        <div>
          <div className="label-eyebrow mb-2">{t("marketplace")}</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("order_now")}</h1>
          <p className="text-muted-foreground mt-1">{t("portal_sub")}</p>
        </div>

        {/* Catalogue */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
          {products.map((p) => (
            <Card key={p.id} className="card-hover border-border" data-testid={`catalog-${p.id}`}>
              <CardContent className="p-4">
                <div className="font-semibold">{p.name}</div>
                <div className="text-xs text-muted-foreground">/ {p.unit}</div>
                <div className="text-lg font-bold text-primary mt-2">{formatXAF(p.price)}</div>
                {p.price_tiers && p.price_tiers.length > 1 && (
                  <div className="mt-2 pt-2 border-t border-border/60 space-y-0.5">
                    {p.price_tiers.map((tier) => (
                      <div key={tier.min_qty} className="flex justify-between text-[11px] text-muted-foreground">
                        <span>{t("from_qty")} {tier.min_qty} {p.unit}</span>
                        <span className="font-medium text-foreground">{formatXAF(tier.price)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Commander */}
          <Card className="border-border">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <ShoppingBasket className="w-5 h-5 text-primary" />{t("your_order")}
              </h3>
              {success ? (
                <div className="rounded-lg bg-primary/10 p-5 text-center" data-testid="order-success">
                  <PackageCheck className="w-10 h-10 text-primary mx-auto mb-3" />
                  <div className="font-semibold text-lg">{t("order_success")}</div>
                  <div className="text-sm text-muted-foreground mt-1">{t("keep_ref")}</div>
                  <div className="mt-3 text-2xl font-bold font-mono tracking-wider text-primary" data-testid="order-ref">{success.ref}</div>
                  <div className="text-sm mt-1">{formatXAF(success.total)}</div>
                  <Button variant="outline" className="mt-4" onClick={() => setSuccess(null)}>{t("order_now")}</Button>
                </div>
              ) : (
                <form onSubmit={submitOrder} className="space-y-3" data-testid="order-form">
                  <div><Label>{t("name")}</Label><Input required data-testid="portal-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{t("phone")}</Label><Input required data-testid="portal-phone" placeholder="+237 …" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                    <div><Label>{t("city")}</Label><Input data-testid="portal-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                  </div>
                  <div>
                    <Label>{t("product")}</Label>
                    <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                      <SelectTrigger data-testid="portal-product"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatXAF(p.price)}/{p.unit}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("quantity")}</Label><Input required type="number" min="1" step="0.5" data-testid="portal-qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
                  <div>
                    <Label>{t("recurrence")}</Label>
                    <Select value={form.recurrence || "aucune"} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                      <SelectTrigger data-testid="portal-recurrence"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aucune">{t("aucune")}</SelectItem>
                        <SelectItem value="hebdomadaire">{t("hebdomadaire")}</SelectItem>
                        <SelectItem value="mensuelle">{t("mensuelle")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="portal-submit">
                    {loading ? "…" : t("order_now")}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Suivi */}
          <Card className="border-border">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-primary" />{t("track_order")}
              </h3>
              <form onSubmit={track} className="flex gap-2 mb-4">
                <Input placeholder="CMD-1234" value={trackRef} onChange={(e) => setTrackRef(e.target.value)} data-testid="track-input" />
                <Button type="submit" variant="outline" data-testid="track-btn">{t("track")}</Button>
              </form>
              {tracked && (
                <div className="space-y-4" data-testid="track-result">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">{tracked.product_name} × {tracked.quantity}</div>
                      <div className="text-xs text-muted-foreground">{tracked.ref} · {formatDate(tracked.order_date, lang)} · {tracked.destination}</div>
                    </div>
                    <Badge variant="secondary">{formatXAF(tracked.total)}</Badge>
                  </div>
                  <div className="flex items-center">
                    {PIPELINE.map((s, i) => (
                      <React.Fragment key={s}>
                        <div className="flex flex-col items-center">
                          <div className={`w-4 h-4 rounded-full border-2 ${i <= statusIndex ? "bg-primary border-primary" : "bg-transparent border-border"}`} />
                          <span className={`mt-1 text-[11px] whitespace-nowrap ${i <= statusIndex ? "text-primary font-medium" : "text-muted-foreground"}`}>{t(s)}</span>
                        </div>
                        {i < PIPELINE.length - 1 && (
                          <div className={`flex-1 h-0.5 mx-1 mb-4 ${i < statusIndex ? "bg-primary" : "bg-border"}`} />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <ContactFooter />
    </div>
  );
}
